/**
 * WebSocket service for real-time chat delivery (staff<->customer
 * conversations -- see mavis_backend's `chat` app). Mirrors
 * pages/tickets/websocketService.js's connection/reconnect pattern, just
 * pointed at ws/chat/ and with chat's own message types
 * (chat_message, chat_added_to_conversation).
 *
 * Unrelated to Device Chat (SMS-with-the-glasses) -- that feature has no
 * WebSocket at all, this is a completely separate channel.
 */

class ChatWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.isConnecting = false;
    this.shouldReconnect = true;
  }

  getWebSocketUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDevelopment) {
      return `ws://127.0.0.1:8000/ws/chat/`;
    }
    return `${protocol}//${window.location.host}/ws/chat/`;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.isConnecting) return;

    try {
      this.isConnecting = true;
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        this.isConnecting = false;
        return;
      }

      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(`${wsUrl}?token=${token}`);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyListeners('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[ChatWS] Error parsing message:', error, event.data);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[ChatWS] error:', error);
        this.isConnecting = false;
        this.notifyListeners('error', { error });
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.notifyListeners('connection', { status: 'disconnected' });

        if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => this.connect(), this.reconnectDelay);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          this.notifyListeners('connection', { status: 'failed' });
        }
      };
    } catch (error) {
      console.error('[ChatWS] Error creating connection:', error);
      this.isConnecting = false;
    }
  }

  handleMessage(data) {
    const { type, payload } = data;
    switch (type) {
      case 'chat_message':
        this.notifyListeners('chat_message', payload);
        break;
      case 'chat_added_to_conversation':
        this.notifyListeners('chat_added_to_conversation', payload);
        break;
      case 'connection_established':
        break;
      default:
        console.warn('[ChatWS] Unknown message type:', type);
    }
  }

  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => {
      const set = this.listeners.get(event);
      if (set) set.delete(callback);
    };
  }

  notifyListeners(event, data) {
    const set = this.listeners.get(event);
    if (set) {
      set.forEach((cb) => {
        try { cb(data); } catch (e) { console.error('[ChatWS] listener error:', e); }
      });
    }
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;
